import type { FilterColumnList } from '@/collections/filter_columns';

// State the helper needs from ReportPage.vue. Kept narrow so the function
// stays pure and the test can drive it without spinning up a Vue instance.
export interface ReportPageSourceState {
  // reportFilters is the live FilterColumnList v-model'd on <filter-column>.
  // filterColumns is the downstream copy maintained by onReportFiltersChanged
  // (gated on readyToGetReport) and can lag. The helper prefers reportFilters
  // when it has items so the dialog inherits whatever the user sees on screen.
  reportFilters: FilterColumnList | null | undefined;
  filterColumns: FilterColumnList | null | undefined;
  dates: string[] | null | undefined;
  reportParams: any;
  productSegment: any;
  // Pass 2 (AS-9964) fields. Optional; helpers omit them from the bundle
  // when falsy so legacy consumers keep working.
  analyticsType?: any;
  comparisonDateModel?: any;
  pivotOption?: string;
  pageOptions?: { sortBy?: any; sortDesc?: any; [k: string]: any };
}

export interface ReportPageSourceReport {
  filterColumns: FilterColumnList | null | undefined;
  filterTimerange: string[] | null | undefined;
  reportParams: any;
  productSegment: any;
  analyticsType?: any;
  comparisonDate?: any;
  pivotOption?: string;
  sortBy?: any;
  sortDesc?: any;
}

// Builds the `sourceReport` bundle ReportPage hands to the Add-to-Purchase-List
// dialog (via `reportStrategy` and `fetchExpansionRows`). Forwarding more
// inputs lets the by_product variant expansion reproduce the source report's
// numbers — see ai-playbooks/docs/features/purchase-list-suggestion-reuse/
// stories/01-suggestion-reuse.md §4.4.
export function buildReportPageSourceReport(state: ReportPageSourceState): ReportPageSourceReport {
  const liveFilters = (state.reportFilters && state.reportFilters.items.length > 0)
    ? state.reportFilters
    : state.filterColumns;
  const bundle: ReportPageSourceReport = {
    filterColumns: liveFilters,
    filterTimerange: state.dates,
    reportParams: state.reportParams,
    productSegment: state.productSegment,
  };
  // Guard by meaningful value, not object truthiness — ReportPage initializes
  // analyticsType/comparisonDateModel to empty model instances at component
  // construction, so a bare `if (state.analyticsType)` would always pass and
  // forward an empty model on every dialog open.
  if (state.analyticsType?.code) { bundle.analyticsType = state.analyticsType; }
  if (state.comparisonDateModel?.code) { bundle.comparisonDate = state.comparisonDateModel; }
  if (state.pivotOption) { bundle.pivotOption = state.pivotOption; }
  if (state.pageOptions?.sortBy) { bundle.sortBy = state.pageOptions.sortBy; }
  if (state.pageOptions?.sortDesc) { bundle.sortDesc = state.pageOptions.sortDesc; }
  return bundle;
}
